/**
 * OrgWorkStatusPage - 조직 근로 현황 페이지
 * V135: 팀관리 → 조직관리 메뉴 개편
 * V5.19: 실제 기능 페이지 구현
 * V61: formatHours toFixed 크래시 방어 처리
 * V62: BE→FE 필드명 정규화 레이어 추가 (단일 지점에서 매핑)
 * V63: normalizeEmployeeStats를 공용 유틸로 이동
 * V65: 조직관리 3페이지 공용 정렬 적용 (직책→직위→입사일→이름)
 * V233: CF 호출 경로 전면 안정화
 *       - getFunctions import 경로 정상화 (V232)
 *       - auth 준비 전 호출 차단 (useCurrentUser guard)
 *       - React 18 StrictMode 이중 마운트 방어 (ignore flag)
 * V70.6: 리스트 헤더 고정 + 엑셀 다운로드
 *       - 헤더 고정: HrLeaveOverviewPage 운영 패턴 재사용
 *         (루트 높이 제한 flex column → 테이블 wrapper 만 가로+세로 스크롤 → th sticky)
 *       - 엑셀: V70.4 휴가현황 엑셀 패턴 재사용 (xlsx). 이미 로드된 sortedEmployees 전체만 사용,
 *         추가 callable/Firestore 조회 없음 → 화면 조회범위 == 엑셀 범위
 * V70.6: 직원 이름 검색 (FE client-side filtering)
 *       - 이미 권한 scope 가 적용되어 로드된 employees 안에서만 이름 부분일치 필터 → 권한 범위 확대 없음
 *       - sortedEmployees → filteredEmployees (정렬 순서 유지) → 화면 == 엑셀
 *       - 월 이동 시 검색어 유지, 추가 callable/Firestore 조회 없음
 *
 * 기능:
 * - 자신의 조직 + 하위 조직 인원들의 월간 근태/근로 요약 조회
 * - 월 이동 UI (전월/다음월, 현재월까지만)
 * - 10가지 지표: 근무일수, 소정근로, 실제근로, 지각, 연장근무, 대휴발생/사용, 미출근, 연차, 공가
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight, Loader2, AlertCircle, Download, Search } from 'lucide-react';
import * as XLSX from 'xlsx';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';
import {
  EmployeeMonthlyStats,
  normalizeEmployeeStats,
} from '../../shared/utils/normalizeOrgWorkStatus';
// V65: 공용 정렬 유틸
import { useJobTitles } from '../../shared/hooks/useJobTitles';
import { usePositionLevels } from '../../shared/hooks/usePositionLevels';
import {
  compareOrgMember,
  buildJobTitleMaps,
  buildPositionLevelMaps,
  OrgMemberSortMaps,
} from '../../shared/utils/sortOrgMembers';
// V66: 직원별 월간 출퇴근 기록 모달
import { EmployeeMonthlyAttendanceModal } from './components/EmployeeMonthlyAttendanceModal';

interface GetOrgWorkStatusMonthlyResponse {
  success: boolean;
  data: {
    month: string;
    scopeMode: string;
    employees: any[]; // BE raw 타입 (normalize 후 FE 타입으로 변환)
    total: number;
    totalCount?: number; // BE에서 totalCount로 반환
    /** V70.8: BE가 실제 집계에 사용한 회사별 기간 (FE 재계산 금지 — 그대로 표시) */
    periods?: Record<string, { startDate: string; endDate: string }>;
    /** V70.8b: 회사별 선택 가능한 최대 월 (KR=현재월, CN=귀속월). FE 날짜 계산 금지 */
    maxSelectableMonths?: { KR: string; CN: string };
  };
  error?: string;
}

/**
 * 현재 월을 YYYY-MM 형식으로 반환
 */
function getCurrentMonth(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * 월 이동 함수
 */
function addMonths(monthStr: string, delta: number): string {
  const [year, month] = monthStr.split('-').map(Number);
  const date = new Date(year, month - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * 월 표시 형식 (예: 2026년 1월)
 */
function formatMonthDisplay(monthStr: string): string {
  const [year, month] = monthStr.split('-').map(Number);
  return `${year}년 ${month}월`;
}

/**
 * V70.6: 로컬 날짜 YYYYMMDD (파일명용)
 * toISOString() 은 UTC 라 KST 00:00~08:59 에 전날 날짜가 되므로 사용하지 않는다.
 */
function formatLocalYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
}

/**
 * V70.6: 엑셀 행 산출 — 화면 테이블 컬럼과 동일 순서·동일 값 규칙 (조직 11열 / 전사 10열 — V70-S-3)
 *
 * - 문자 컬럼: 화면과 동일하게 값 없으면 '-'
 * - 숫자 컬럼: Excel 숫자형으로 저장 (화면의 NaN 방어 '—' 에 해당하는 경우만 '-')
 * - 시간 컬럼: 화면 formatHours(toFixed(1)) 와 동일하게 소수 1자리 반올림한 **숫자**
 */
function toExportNumber(value: number | undefined | null): number | string {
  return typeof value === 'number' && !Number.isNaN(value) ? value : '-';
}

function toExportHours(value: number | undefined | null): number | string {
  return typeof value === 'number' && !Number.isNaN(value) ? Number(value.toFixed(1)) : '-';
}

/**
 * V70-S-3: includeAnnual=false 면 '연차' 열을 뺀다 (전사 근로 현황 — 화면 10열과 일치).
 *          기본값 true = 조직 근로 현황 기존 11열 그대로.
 */
export function deriveWorkStatusExportRow(
  emp: EmployeeMonthlyStats,
  { includeAnnual = true }: { includeAnnual?: boolean } = {},
): Record<string, string | number> {
  const row: Record<string, string | number> = {
    '이름': emp.name || '-',
    '조직': emp.orgUnitName || '-',
    '직위': emp.positionName || '-',
    '직책': emp.jobTitle || '-',
    '근무일수': toExportNumber(emp.workDays),
    '소정근로': toExportHours(emp.scheduledHours),
    '실제근로': toExportHours(emp.actualHours),
    '지각': toExportNumber(emp.lateCount),
    '연장근무': toExportHours(emp.overtimeHours),
    '미출근': toExportNumber(emp.absenceCount),
    '연차': toExportNumber(emp.annualUsed),
  };
  if (!includeAnnual) delete row['연차'];
  return row;
}

/**
 * V70-S: 화면 변형
 *  - 'org'  (기본) : 조직관리 > 조직 근로 현황 — 기존 동작·문구·callable 그대로
 *  - 'hrKr'        : 인사관리 > 전사 근로 현황 — 같은 표·지표·엑셀·상세 모달, 조회 대상만 KR 전사원
 *                    (callable getHrKrWorkStatusMonthly, 권한 system_admin·role_admin_kr·KR role_hr)
 */
export type OrgWorkStatusVariant = 'org' | 'hrKr';

/**
 * V70-S-3: 테이블 컬럼 너비 (tableLayout fixed + width 100% → 남는 폭은 비율대로 분배, 합계 = 좁은 화면 최소 폭)
 *  - org  : 기존 11개 컬럼 그대로
 *  - hrKr : '연차' 컬럼 제거 → 그 56px 를 텍스트 컬럼(이름 +8 / 조직 +28 / 직위 +10 / 직책 +10)에 재분배.
 *           숫자 컬럼 폭은 기존과 동일, 합계 748px 유지 (빈 열 없이 꽉 참)
 */
const ORG_COL_WIDTHS = { name: 80, org: 100, position: 64, jobTitle: 64, annual: 56 };
const HR_KR_COL_WIDTHS = { name: 88, org: 128, position: 74, jobTitle: 74, annual: 0 };

const VARIANT_CONFIG: Record<OrgWorkStatusVariant, {
  callableName: string;
  title: string;
  guides: string[];
  sheetName: string;
  filePrefix: string;
  scopeSuffix: (scopeMode: string) => string;
  /** V70-S-3: 화면 테이블 '연차' 컬럼 표시 여부 (엑셀은 변경하지 않음) */
  showAnnualColumn: boolean;
  colWidths: typeof ORG_COL_WIDTHS;
}> = {
  org: {
    showAnnualColumn: true,
    colWidths: ORG_COL_WIDTHS,
    callableName: 'getOrgWorkStatusMonthly',
    title: '조직 근로 현황',
    guides: ['· 조직원들의 근로 현황을 확인할 수 있습니다.', '· 직원 이름을 클릭하면 월별 출퇴근 기록을 확인할 수 있습니다.'],
    sheetName: '조직근로현황',
    filePrefix: '조직근로현황',
    scopeSuffix: (scopeMode) => (scopeMode === 'ALL' ? ' (전체 조회)' : ''),
  },
  hrKr: {
    showAnnualColumn: false,
    colWidths: HR_KR_COL_WIDTHS,
    callableName: 'getHrKrWorkStatusMonthly',
    title: '전사 근로 현황',
    guides: [
      '· KR 전사원(재직·휴직)의 근로 현황입니다. 지표 정의는 조직관리 > 조직 근로 현황과 같습니다.',
      '· 연장근무는 승인된 근무 신청 시간 합계입니다(연장·휴일 구분 없음). 연장·야간·휴일 구분은 인사관리 > 연장 근무 관리에서 확인하세요.',
      '· 직원 이름을 클릭하면 월별 출퇴근 기록을 확인할 수 있습니다.',
    ],
    sheetName: '전사근로현황',
    filePrefix: '전사근로현황_KR',
    scopeSuffix: () => ' (KR 전사)',
  },
};

export function OrgWorkStatusPage({ variant = 'org' }: { variant?: OrgWorkStatusVariant } = {}) {
  const config = VARIANT_CONFIG[variant];
  // V233: auth 준비 전 호출 차단
  const { user, isLoading: authLoading } = useCurrentUser();

  const currentMonth = getCurrentMonth();
  const [month, setMonth] = useState<string>(currentMonth);
  const [employees, setEmployees] = useState<EmployeeMonthlyStats[]>([]);
  const [scopeMode, setScopeMode] = useState<string>('');
  // V70.8: BE가 계산한 회사별 집계기간 (CN 포함 시 월 이동 UI 아래에 표시)
  const [periods, setPeriods] = useState<Record<string, { startDate: string; endDate: string }> | null>(null);
  // V70.8b: BE가 알려준 회사별 최대 선택월 (월 이동 상한 SSoT)
  const [maxMonth, setMaxMonth] = useState<string | null>(null);
  // V70.8b: CN 전용 화면에서 귀속월로 1회만 자동 이동 (사용자가 이후 직접 이동한 월을 덮어쓰지 않음)
  const cnMonthAdjustedRef = useRef(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // V66: 직원별 월간 출퇴근 기록 모달 상태
  const [attendanceModalEmployee, setAttendanceModalEmployee] = useState<{ id: string; name: string } | null>(null);

  // V70.6: 엑셀 다운로드 중 중복 클릭 방지 (V70.4 휴가현황과 동일 패턴)
  const [isExporting, setIsExporting] = useState(false);

  // V70.6: 직원 이름 검색어 (월 이동 시에도 유지)
  const [searchText, setSearchText] = useState('');

  // V233: StrictMode 이중 마운트 방어 - 진행 중인 요청 추적
  const fetchIdRef = useRef(0);

  // ============================================
  // V65: 공용 정렬 적용 (마스터 데이터 로딩)
  // ============================================
  const { jobTitles } = useJobTitles({ status: 'active' });
  const { positionLevels } = usePositionLevels({ status: 'active' });

  // 정렬 맵 생성 (이름 기반 조회 - BE에서 ID 대신 이름만 반환)
  const sortMaps: OrgMemberSortMaps = useMemo(() => {
    const jobTitleMaps = buildJobTitleMaps(jobTitles);
    const positionLevelMaps = buildPositionLevelMaps(positionLevels);
    return {
      ...jobTitleMaps,
      ...positionLevelMaps,
    };
  }, [jobTitles, positionLevels]);

  // 정렬된 직원 목록
  const sortedEmployees = useMemo(() => {
    return [...employees].sort((a, b) => compareOrgMember(a, b, sortMaps));
  }, [employees, sortMaps]);

  // V70.6: 이름 검색 (앞뒤 공백 무시, 부분일치). 정렬된 배열을 필터하므로 기존 정렬 순서 유지.
  // 검색어가 없으면 sortedEmployees 그대로 → 기존 화면과 완전히 동일.
  const searchKeyword = searchText.trim().toLowerCase();
  const filteredEmployees = useMemo(() => {
    if (!searchKeyword) return sortedEmployees;
    return sortedEmployees.filter((emp) => (emp.name || '').toLowerCase().includes(searchKeyword));
  }, [sortedEmployees, searchKeyword]);

  /**
   * 데이터 조회
   * V233: fetchId를 받아, 응답 도착 시 최신 요청인지 검증 (StrictMode 방어)
   */
  const fetchData = useCallback(async (targetMonth: string, currentFetchId?: number) => {
    setLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const getOrgWorkStatusMonthlyFn = httpsCallable<{ month: string }, GetOrgWorkStatusMonthlyResponse>(
        functions,
        config.callableName
      );

      const result = await getOrgWorkStatusMonthlyFn({ month: targetMonth });

      // V233: StrictMode 방어 - 이미 취소된 요청이면 state 업데이트 스킵
      if (currentFetchId !== undefined && currentFetchId !== fetchIdRef.current) {
        return;
      }

      if (result.data.success) {
        // V62: BE→FE 필드명 정규화 (단일 지점)
        const normalizedEmployees = result.data.data.employees.map(normalizeEmployeeStats);
        setEmployees(normalizedEmployees);
        setScopeMode(result.data.data.scopeMode);
        // V70.8: 집계기간은 BE 응답 그대로 사용 (화면 표시 == 실제 집계 기간)
        const responsePeriods = result.data.data.periods ?? null;
        setPeriods(responsePeriods);

        // V70.8b: 월 이동 상한 / CN 기본 귀속월
        //  - CN 전용 화면(응답에 CN 만 존재) → CN 귀속월(BE 계산값)이 상한이자 기본월
        //  - KR 포함(KR 전용·KR+CN 혼합) → 기존 현재월 상한 유지
        const maxMonths = result.data.data.maxSelectableMonths;
        if (maxMonths) {
          const isCnOnly = !!responsePeriods?.CN && !responsePeriods?.KR;
          const limit = isCnOnly ? maxMonths.CN : maxMonths.KR;
          setMaxMonth(limit);
          // CN 전용 화면 최초 진입 시, 진행 중인 귀속월로 1회 이동 (예: 9/16 이후 → 2026-10)
          if (isCnOnly && !cnMonthAdjustedRef.current && targetMonth < maxMonths.CN) {
            cnMonthAdjustedRef.current = true;
            setMonth(maxMonths.CN);
          }
        }
      } else {
        setError(result.data.error || '데이터 조회 실패');
      }
    } catch (err: any) {
      // V233: 취소된 요청의 에러는 무시
      if (currentFetchId !== undefined && currentFetchId !== fetchIdRef.current) {
        return;
      }
      console.error('[OrgWorkStatusPage] 데이터 조회 실패:', err);
      setError(err.message || '서버 오류가 발생했습니다.');
    } finally {
      if (currentFetchId === undefined || currentFetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [config.callableName]);

  /**
   * 월 변경 시 데이터 재조회
   * V233: auth 준비 전 호출 차단 + StrictMode 이중 마운트 방어
   */
  useEffect(() => {
    // V233: auth가 아직 로딩 중이거나 user가 없으면 CF 호출 차단
    if (authLoading || !user) {
      return;
    }

    // V233: fetchId 증가 → 이전 요청 응답이 도착해도 state 업데이트 안 됨
    const id = ++fetchIdRef.current;
    fetchData(month, id);
  }, [month, user, authLoading, fetchData]);

  /**
   * 이전 월로 이동
   */
  const handlePrevMonth = () => {
    setMonth((prev) => addMonths(prev, -1));
  };

  /**
   * 다음 월로 이동
   */
  const handleNextMonth = () => {
    setMonth((prev) => addMonths(prev, 1));
  };

  /**
   * 다음 월 버튼 표시 여부 (현재월 이후는 불가)
   */
  // V70.8b: 상한은 BE가 알려준 값 우선 (CN 전용=귀속월, 그 외=현재월). 응답 전에는 기존 현재월 기준.
  const canGoNext = month < (maxMonth ?? currentMonth);

  /**
   * V70.6: 엑셀 다운로드
   *
   * 범위: 현재 선택 월(`month`)로 조회되어 **화면에 표시 중인** `filteredEmployees` — 화면과 같은 정렬.
   *   검색어가 없으면 전체, 검색 중이면 검색 결과만 (화면 표시 배열 == 엑셀 배열).
   *   권한 범위는 getOrgWorkStatusMonthly → orgManagementScope(BE SSoT) 가 이미 적용한 결과이며
   *   FE 에서 권한 판정을 새로 하지 않는다. 추가 callable / Firestore 조회 없음.
   * 값: deriveWorkStatusExportRow — 화면 컬럼과 동일 순서·동일 규칙 (V70-S-3: 전사 근로 현황은 연차 제외).
   */
  const handleExcelDownload = () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      if (filteredEmployees.length === 0) {
        alert('다운로드할 데이터가 없습니다.');
        return;
      }

      // V70-S-3: 엑셀 열 = 화면 열 (전사 근로 현황은 연차 제외 10열, 조직 근로 현황은 11열)
      const exportData = filteredEmployees.map((emp) => deriveWorkStatusExportRow(emp, { includeAnnual: config.showAnnualColumn }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const headers = Object.keys(exportData[0]);
      worksheet['!cols'] = headers.map((h) => ({
        wch: h === '조직' ? 20 : Math.max(h.length * 2, 10),
      }));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, config.sheetName);

      XLSX.writeFile(workbook, `${config.filePrefix}_${month}_${formatLocalYmd(new Date())}.xlsx`);
    } catch (err: any) {
      console.error('[OrgWorkStatusPage] V70.6 엑셀 다운로드 실패:', err);
      alert(err?.message || '엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setIsExporting(false);
    }
  };

  const exportDisabled = isExporting || loading || !!error;

  /**
   * V70.8: CN 집계기간 표시 문자열
   * - BE 응답 periods.CN 이 있을 때만 (= 조회 결과에 CN 직원이 포함된 경우)
   * - 기간은 BE 가 실제 집계에 사용한 값 그대로 (FE 재계산 없음)
   */
  const cnPeriod = periods?.CN;
  const cnPeriodText = cnPeriod
    ? `CN 집계기간 ${cnPeriod.startDate.replace(/-/g, '.')} ~ ${cnPeriod.endDate.replace(/-/g, '.')}`
    : null;

  return (
    // V70.6: 높이 제한 flex column — 테이블 wrapper 만 내부 스크롤 (HrLeaveOverviewPage 동일 값)
    <div style={{
      padding: '24px',
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 120px)',
      minHeight: 0,
      overflow: 'hidden',
    }}>
      {/* 헤더 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        flexShrink: 0,
      }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: 'bold',
        }}>
          {config.title}
        </h1>

        {/* V70.6: 우측 영역 — 엑셀 다운로드 + 월 이동 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* V70.6: 엑셀 다운로드 (선택 월 · 화면 조회범위 전체, 화면과 동일 값) */}
          <button
            onClick={handleExcelDownload}
            disabled={exportDisabled}
            title={isExporting ? '다운로드 중...' : `${config.title} 엑셀 다운로드`}
            style={{
              padding: '8px 16px',
              background: exportDisabled ? 'var(--color-gray-400)' : 'var(--color-green-600)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: exportDisabled ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Download size={16} />
            {isExporting ? '다운로드 중...' : '엑셀 다운로드'}
          </button>

          {/* 월 이동 UI (V70.8: 아래에 CN 집계기간 한 줄이 붙을 수 있어 column 래퍼 추가) */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '4px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              background: 'var(--color-gray-100)',
              padding: '8px 16px',
              borderRadius: '8px'
            }}>
            <button
              onClick={handlePrevMonth}
              style={{
                padding: '4px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                color: 'var(--color-gray-600)'
              }}
              title="이전 월"
            >
              <ChevronLeft size={20} />
            </button>

            <span style={{
              fontSize: '16px',
              fontWeight: '600',
              minWidth: '120px',
              textAlign: 'center'
            }}>
              {formatMonthDisplay(month)}
            </span>

            {canGoNext ? (
              <button
                onClick={handleNextMonth}
                style={{
                  padding: '4px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--color-gray-600)'
                }}
                title="다음 월"
              >
                <ChevronRight size={20} />
              </button>
            ) : (
              <div style={{ width: '28px' }} /> /* 공간 유지 */
            )}
            </div>

            {/* V70.8: CN(대련지사) 집계기간 — 응답에 CN 직원이 포함된 경우에만 표시.
                       값은 BE가 실제 집계에 사용한 기간 그대로 (FE 재계산 없음).
                       KR 전용 화면에서는 이 줄 자체가 렌더링되지 않음 */}
            {cnPeriodText && (
              <div style={{
                fontSize: '12px',
                color: 'var(--color-gray-500)',
                textAlign: 'center',
                whiteSpace: 'nowrap',
              }}>
                {cnPeriodText}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* V68: 페이지 가이드 안내 */}
      <div style={{ marginBottom: '24px', color: '#94a3b8', fontSize: '13px', lineHeight: 1.6, flexShrink: 0 }}>
        {config.guides.map((g) => <p key={g} style={{ margin: 0 }}>{g}</p>)}
      </div>

      {/* V70.6: 검색 + 결과 요약 — 테이블 스크롤 영역 밖(flexShrink 0)이라 목록을 스크롤해도 유지.
          로딩/에러 중에도 input 을 유지해 월 이동 시 검색어·포커스가 초기화되지 않음 */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        flexWrap: 'wrap',
        marginBottom: '16px',
        flexShrink: 0,
      }}>
        {/* 검색 입력 (OrgMembersPage 검색 입력 패턴) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'var(--color-gray-50)',
            borderRadius: '6px',
            padding: '6px 12px',
            border: '1px solid var(--color-gray-200)',
          }}
        >
          <Search size={16} style={{ color: 'var(--color-gray-400)' }} />
          <input
            type="text"
            placeholder="직원 이름 검색"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              border: 'none',
              background: 'transparent',
              outline: 'none',
              fontSize: '13px',
              width: '180px',
            }}
          />
        </div>

        {/* 결과 요약 — V65: sortedEmployees(공용 정렬), V70.6: 검색 중이면 검색 결과 / 전체 */}
        {!loading && !error && (
          <div style={{
            fontSize: '14px',
            color: 'var(--color-gray-600)',
          }}>
            {searchKeyword
              ? `검색 결과 ${filteredEmployees.length}명 / 전체 ${sortedEmployees.length}명`
              : `총 ${sortedEmployees.length}명`}
            {config.scopeSuffix(scopeMode)}
          </div>
        )}
      </div>

      {/* 로딩 상태 */}
      {loading && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '60px',
          color: 'var(--color-gray-500)',
          flexShrink: 0,
        }}>
          <Loader2 size={32} className="animate-spin" style={{ marginRight: '12px' }} />
          <span>데이터를 불러오는 중...</span>
        </div>
      )}

      {/* 에러 상태 */}
      {!loading && error && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '60px',
          background: 'var(--color-red-50)',
          borderRadius: '8px',
          color: 'var(--color-red-600)',
          flexShrink: 0,
        }}>
          <AlertCircle size={48} style={{ marginBottom: '16px' }} />
          <p style={{ fontSize: '16px', marginBottom: '8px' }}>오류가 발생했습니다</p>
          <p style={{ fontSize: '14px', color: 'var(--color-red-500)' }}>{error}</p>
          <button
            onClick={() => fetchData(month)}
            style={{
              marginTop: '16px',
              padding: '8px 16px',
              background: 'var(--color-red-600)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            다시 시도
          </button>
        </div>
      )}

      {/* 데이터 테이블 */}
      {!loading && !error && (
        <>
          {filteredEmployees.length === 0 ? (
            <div style={{
              padding: '60px',
              background: 'var(--color-gray-50)',
              borderRadius: '8px',
              textAlign: 'center',
              color: 'var(--color-gray-500)',
              flexShrink: 0,
            }}>
              {/* V70.6: 원본 0명 vs 검색 결과 0명 구분 */}
              <p>{sortedEmployees.length === 0 ? '조회된 데이터가 없습니다.' : '검색 결과가 없습니다.'}</p>
            </div>
          ) : (
            // V70.6: wrapper 가 가로+세로 스크롤 담당 → th sticky 기준 컨테이너 (HrLeaveOverviewPage 동일)
            <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
              <table data-testid="work-status-table" style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px',
                tableLayout: 'fixed'
              }}>
                <colgroup>
                  <col style={{ width: `${config.colWidths.name}px` }} />      {/* 이름 */}
                  <col style={{ width: `${config.colWidths.org}px` }} />       {/* 조직 */}
                  <col style={{ width: `${config.colWidths.position}px` }} />  {/* 직위 */}
                  <col style={{ width: `${config.colWidths.jobTitle}px` }} />  {/* 직책 */}
                  <col style={{ width: '64px' }} />   {/* 근무일수 */}
                  <col style={{ width: '72px' }} />   {/* 소정근로 */}
                  <col style={{ width: '72px' }} />   {/* 실제근로 */}
                  <col style={{ width: '48px' }} />   {/* 지각 */}
                  <col style={{ width: '72px' }} />   {/* 연장근무 */}
                  <col style={{ width: '56px' }} />   {/* 미출근 */}
                  {config.showAnnualColumn && <col style={{ width: `${config.colWidths.annual}px` }} />}  {/* 연차 (V70-S-3: hrKr 미표시) */}
                </colgroup>
                <thead>
                  {/* V70.6: 배경·하단선은 sticky th(thStyle)로 이동 — tr 배경은 sticky 시 따라오지 않음 */}
                  <tr>
                    <th style={thStyle}>이름</th>
                    <th style={thStyle}>조직</th>
                    <th style={thStyle}>직위</th>
                    <th style={thStyle}>직책</th>
                    <th style={{ ...thStyle, ...numericStyle }}>근무일수</th>
                    <th style={{ ...thStyle, ...numericStyle }}>소정근로</th>
                    <th style={{ ...thStyle, ...numericStyle }}>실제근로</th>
                    <th style={{ ...thStyle, ...numericStyle }}>지각</th>
                    <th style={{ ...thStyle, ...numericStyle }}>연장근무</th>
                    <th style={{ ...thStyle, ...numericStyle }}>미출근</th>
                    {config.showAnnualColumn && <th style={{ ...thStyle, ...numericStyle }}>연차</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map((emp) => (
                    <tr
                      key={emp.userId}
                      style={{ borderBottom: '1px solid var(--color-gray-200)' }}
                    >
                      {/* V66: 이름 클릭 → 직원별 월간 출퇴근 기록 모달 */}
                      <td style={tdStyle}>
                        <span
                          onClick={() => setAttendanceModalEmployee({ id: emp.userId, name: emp.name || '-' })}
                          style={{ cursor: 'pointer', textDecoration: 'underline', color: 'inherit' }}
                          title="월별 출퇴근 기록 보기"
                        >
                          {emp.name}
                        </span>
                      </td>
                      <td style={tdStyle}>{emp.orgUnitName || '-'}</td>
                      <td style={tdStyle}>{emp.positionName || '-'}</td>
                      <td style={tdStyle}>{emp.jobTitle || '-'}</td>
                      <td style={{ ...tdStyle, ...numericStyle }}>{safeNumber(emp.workDays)}</td>
                      <td style={{ ...tdStyle, ...numericStyle }}>{formatHours(emp.scheduledHours)}</td>
                      <td style={{ ...tdStyle, ...numericStyle }}>{formatHours(emp.actualHours)}</td>
                      <td style={{ ...tdStyle, ...numericStyle, ...lateStyle(emp.lateCount) }}>
                        {safeNumber(emp.lateCount)}
                      </td>
                      <td style={{ ...tdStyle, ...numericStyle }}>{formatHours(emp.overtimeHours)}</td>
                      <td style={{ ...tdStyle, ...numericStyle, ...absenceStyle(emp.absenceCount) }}>
                        {safeNumber(emp.absenceCount)}
                      </td>
                      {config.showAnnualColumn && <td style={{ ...tdStyle, ...numericStyle }}>{safeNumber(emp.annualUsed)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* V66: 직원별 월간 출퇴근 기록 모달 */}
      <EmployeeMonthlyAttendanceModal
        isOpen={!!attendanceModalEmployee}
        onClose={() => setAttendanceModalEmployee(null)}
        employeeId={attendanceModalEmployee?.id ?? null}
        employeeName={attendanceModalEmployee?.name ?? null}
        initialMonth={month}
        /* V70.8c: 상세 모달의 월 이동 상한 = 목록과 동일 (BE maxSelectableMonths 유래, FE 재계산 없음) */
        maxMonth={maxMonth ?? currentMonth}
      />
    </div>
  );
}

/**
 * 시간 포맷 (소수점 1자리)
 * V61: undefined/null/NaN 방어 처리 추가
 */
function formatHours(hours: number | undefined | null): string {
  if (typeof hours !== 'number' || Number.isNaN(hours)) return '—';
  return hours.toFixed(1);
}

/**
 * 숫자 안전 표시 (정수)
 * V61: undefined/null/NaN 방어 처리
 */
function safeNumber(value: number | undefined | null): string | number {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value;
}

/**
 * 테이블 스타일
 */
const thStyle: React.CSSProperties = {
  padding: '12px 8px',
  textAlign: 'left',
  fontWeight: '600',
  whiteSpace: 'nowrap',
  // V70.6: 헤더 고정 — 불투명 배경 필수(행 비침 방지), z-index 는 모달(1000)보다 낮게
  position: 'sticky',
  top: 0,
  zIndex: 1,
  background: 'var(--color-gray-100)',
  // borderCollapse:collapse 에서 sticky 시 border 가 따라오지 않으므로 inset shadow 로 하단선 유지
  boxShadow: 'inset 0 -2px 0 var(--color-gray-200)',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 8px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const numericStyle: React.CSSProperties = {
  textAlign: 'center'
};

/**
 * 지각 스타일 (1회 이상이면 빨간색)
 * V61: undefined 방어
 */
function lateStyle(count: number | undefined | null): React.CSSProperties {
  if (typeof count === 'number' && count > 0) {
    return { color: 'var(--color-red-600)', fontWeight: '600' };
  }
  return {};
}

/**
 * 미출근 스타일 (1회 이상이면 빨간색)
 * V61: undefined 방어
 */
function absenceStyle(count: number | undefined | null): React.CSSProperties {
  if (typeof count === 'number' && count > 0) {
    return { color: 'var(--color-red-600)', fontWeight: '600' };
  }
  return {};
}
